// GraphiteQuery.js
// Parsing and serialization of Graphite /render query strings and target expressions.
// Used by the admin Graphite configuration GUI (AdminGraphite.vue and related controls).
//
// A "render query string" is one entry from GraphiteConfig.json, e.g.:
//   target=alias(stats.gauges.WEB5.statref.activeSessionCount.db, "Active Session Count")&lineMode=connected&from=-8h&title=Some Title
//
// Width and height parameters are intentionally stripped during parsing and are never emitted by
// serialization; image dimensions are decided at render time by the viewing client (see GraphiteRender.js).
//
// The %%ServerName%% placeholder must survive parse/serialize round trips in literal (unencoded) form,
// because consumers substitute it via string replacement on the raw query string before rendering.

/////////////////////////////
// Query string primitives //
/////////////////////////////

const SERVER_NAME_PLACEHOLDER = /%%ServerName%%/gi;
const PLACEHOLDER_SENTINEL = "\x01ServerNamePlaceholder\x01";

/**
 * Leniently decodes a query string component. Standard decodeURIComponent throws on malformed
 * percent-escapes such as the raw %%ServerName%% placeholder, so those are protected first, and if
 * decoding still fails, the component is returned with only valid escapes decoded.
 * @param {String} str Raw query string component.
 * @returns {String} Decoded text (with %%ServerName%% left intact).
 */
export function DecodeQueryComponent(str)
{
	let s = str.replace(/\+/g, " ").replace(SERVER_NAME_PLACEHOLDER, PLACEHOLDER_SENTINEL);
	let decoded;
	try
	{
		decoded = decodeURIComponent(s);
	}
	catch (ex)
	{
		// Decode only the valid %XX escapes, leaving stray '%' characters alone.
		decoded = s.replace(/%[0-9A-Fa-f]{2}/g, m => decodeURIComponent(m));
	}
	return decoded.split(PLACEHOLDER_SENTINEL).join("%%ServerName%%");
}
/**
 * Encodes a query string component, keeping the %%ServerName%% placeholder in literal form.
 * @param {String} str Decoded text.
 * @returns {String} Encoded query string component.
 */
export function EncodeQueryComponent(str)
{
	return encodeURIComponent(str).replace(/%25%25ServerName%25%25/gi, "%%ServerName%%");
}
/**
 * Splits a query string into decoded key/value pairs, preserving order and duplicate keys.
 * @param {String} queryString Query string without a leading "?".
 * @returns {Array} Array of { key, value } objects.
 */
export function ParseQueryParams(queryString)
{
	let pairs = [];
	if (!queryString)
		return pairs;
	for (let part of queryString.split("&"))
	{
		if (!part)
			continue;
		let eq = part.indexOf("=");
		let key = eq === -1 ? part : part.substr(0, eq);
		let value = eq === -1 ? "" : part.substr(eq + 1);
		pairs.push({ key: DecodeQueryComponent(key), value: DecodeQueryComponent(value) });
	}
	return pairs;
}
/**
 * Builds a query string from decoded key/value pairs.
 * @param {Array} pairs Array of { key, value } objects.
 * @returns {String} Query string without a leading "?".
 */
export function BuildQueryParams(pairs)
{
	return pairs.map(p => EncodeQueryComponent(p.key) + "=" + EncodeQueryComponent(p.value)).join("&");
}

//////////////////////////////////
// Target expression AST/parser //
//////////////////////////////////
// AST node shapes:
//   { t: "call", name: String, args: [node] }
//   { t: "path", v: String }             - metric path, possibly containing wildcards or %%ServerName%%
//   { t: "str",  v: String, q: String }  - quoted string; q is the original quote character
//   { t: "num",  v: String }             - numeric literal kept as its original text
//   { t: "bool", v: Boolean }
//   { t: "none" }                        - Python None

const PATH_CHAR = /[A-Za-z0-9._\-*%{}[\]#$:?+@!^~<>=|\\/]/;

function TokenizeExpression(text)
{
	let tokens = [];
	let i = 0;
	while (i < text.length)
	{
		let c = text.charAt(i);
		if (c === " " || c === "\t" || c === "\r" || c === "\n")
		{
			i++;
			continue;
		}
		if (c === "(" || c === ")" || c === ",")
		{
			tokens.push({ type: c, pos: i });
			i++;
			continue;
		}
		if (c === "\"" || c === "'")
		{
			let end = text.indexOf(c, i + 1);
			if (end === -1)
				throw new Error("Unterminated string starting at position " + i);
			tokens.push({ type: "str", value: text.substring(i + 1, end), quote: c, pos: i });
			i = end + 1;
			continue;
		}
		if (PATH_CHAR.test(c))
		{
			let start = i;
			while (i < text.length && PATH_CHAR.test(text.charAt(i)))
				i++;
			tokens.push({ type: "word", value: text.substring(start, i), pos: start });
			continue;
		}
		throw new Error("Unexpected character \"" + c + "\" at position " + i);
	}
	return tokens;
}
/**
 * Parses a Graphite target expression such as alias(sumSeries(stats.gauges.WEB*.foo),"Total") into an AST.
 * Throws an Error with a descriptive message if the expression cannot be parsed.
 * @param {String} text Target expression text.
 * @returns {Object} AST root node.
 */
export function ParseExpression(text)
{
	let tokens = TokenizeExpression(text);
	let idx = 0;
	function peek() { return idx < tokens.length ? tokens[idx] : null; }
	function next()
	{
		if (idx >= tokens.length)
			throw new Error("Unexpected end of expression");
		return tokens[idx++];
	}
	function parseNode()
	{
		let tok = next();
		if (tok.type === "str")
			return { t: "str", v: tok.value, q: tok.quote };
		if (tok.type !== "word")
			throw new Error("Unexpected \"" + tok.type + "\" at position " + tok.pos);
		let p = peek();
		if (p && p.type === "(")
		{
			next(); // consume "("
			let args = [];
			if (peek() && peek().type === ")")
				next();
			else
			{
				while (true)
				{
					args.push(parseNode());
					let sep = next();
					if (sep.type === ")")
						break;
					if (sep.type !== ",")
						throw new Error("Expected \",\" or \")\" at position " + sep.pos);
				}
			}
			return { t: "call", name: tok.value, args };
		}
		return ClassifyWord(tok.value);
	}
	let root = parseNode();
	let extra = peek();
	if (extra)
		throw new Error("Unexpected content at position " + extra.pos);
	return root;
}
function ClassifyWord(word)
{
	if (/^-?(\d+\.?\d*|\.\d+)(e-?\d+)?$/i.test(word))
		return { t: "num", v: word };
	if (word === "true" || word === "True")
		return { t: "bool", v: true };
	if (word === "false" || word === "False")
		return { t: "bool", v: false };
	if (word === "None" || word === "none")
		return { t: "none" };
	return { t: "path", v: word };
}
/**
 * Serializes an AST node back into Graphite target expression text.
 * @param {Object} node AST node.
 * @returns {String} Expression text.
 */
export function SerializeExpression(node)
{
	if (!node)
		return "";
	switch (node.t)
	{
		case "call":
			return node.name + "(" + node.args.map(SerializeExpression).join(",") + ")";
		case "path":
			return node.v;
		case "str":
			{
				let q = node.q || "\"";
				if (node.v.indexOf(q) !== -1)
					q = q === "\"" ? "'" : "\"";
				return q + node.v + q;
			}
		case "num":
			return String(node.v);
		case "bool":
			return node.v ? "true" : "false";
		case "none":
			return "None";
	}
	throw new Error("Unknown AST node type: " + node.t);
}
/**
 * Returns true if the AST node is a literal (string, number, boolean, or None).
 * @param {Object} node AST node.
 * @returns {Boolean} true if literal.
 */
export function IsLiteralNode(node)
{
	return node && (node.t === "str" || node.t === "num" || node.t === "bool" || node.t === "none");
}

//////////////////////////////////////
// "Function chain" representation  //
//////////////////////////////////////
// Most useful Graphite expressions are a base metric path wrapped by a pipeline of functions where
// each function takes the previous expression as its first argument and literals for the rest, e.g.
//   alias(summarize(stats_counts.WEB5.foo, "1min"), "Foo Per Minute")
//   -> { base: "stats_counts.WEB5.foo", fns: [ { name: "summarize", args: ["1min"] }, { name: "alias", args: ["Foo Per Minute"] } ] }
// Expressions that do not fit this shape (multiple series arguments, nested series expressions, etc.)
// are edited as raw text in the GUI instead.

/**
 * Attempts to convert an expression AST into a { base, fns } function chain.
 * @param {Object} ast AST root node.
 * @returns {Object} { base: String, fns: [{ name: String, args: [literal AST] }] }, or null if the expression does not fit the chain model.
 */
export function TryBuildChain(ast)
{
	let fns = [];
	let node = ast;
	while (node && node.t === "call")
	{
		if (!node.args.length)
			return null;
		let rest = node.args.slice(1);
		if (!rest.every(IsLiteralNode))
			return null;
		let first = node.args[0];
		if (first.t !== "call" && first.t !== "path")
			return null;
		fns.unshift({ name: node.name, args: rest });
		node = first;
	}
	if (!node || node.t !== "path")
		return null;
	return { base: node.v, fns };
}
/**
 * Builds an expression AST from a { base, fns } function chain.
 * @param {String} base Base metric path.
 * @param {Array} fns Array of { name, args } where args are literal AST nodes.
 * @returns {Object} AST root node.
 */
export function BuildAstFromChain(base, fns)
{
	let node = { t: "path", v: base };
	for (let fn of fns)
		node = { t: "call", name: fn.name, args: [node].concat(fn.args) };
	return node;
}

///////////////////////////////
// Render query string model //
///////////////////////////////

/**
 * Metadata describing the curated set of Graphite render options exposed as first-class fields in the
 * graph editor GUI.  Options not in this list round-trip through the "otherParams" collection.
 * Types: "string", "number", "bool" (tri-state: unset/true/false), "select".
 */
export const RENDER_OPTIONS = [
	{ key: "vtitle", label: "Y-Axis Label", type: "string", help: "Text drawn vertically along the left side of the graph, describing the unit or meaning of the Y axis." },
	{
		key: "lineMode", label: "Line Mode", type: "select", choices: ["slope", "staircase", "connected"],
		help: "How lines are drawn between data points. \"slope\" (default) draws straight segments between points and leaves gaps at missing data. \"staircase\" draws stepped lines. \"connected\" draws a line across gaps of missing (null) data to the next known point — recommended for gauges that report intermittently."
	},
	{
		key: "areaMode", label: "Area Fill", type: "select", choices: ["none", "first", "all", "stacked"],
		help: "Fills the area below lines. \"first\" fills only the first series, \"all\" fills every series, \"stacked\" stacks the filled series on top of each other (useful for additive quantities such as counts per server)."
	},
	{ key: "lineWidth", label: "Line Width", type: "number", help: "Line thickness in pixels (Graphite default is 1.2). This is scaled automatically for high-DPI displays at render time." },
	{ key: "yMin", label: "Y Minimum", type: "number", help: "Lowest value shown on the Y axis. Leave empty to size automatically. Commonly set to 0 so small fluctuations aren't exaggerated." },
	{ key: "yMax", label: "Y Maximum", type: "number", help: "Highest value shown on the Y axis. Leave empty to size automatically." },
	{ key: "logBase", label: "Logarithmic Base", type: "number", help: "If set (must be greater than 1, e.g. 10), the Y axis uses a logarithmic scale." },
	{
		key: "drawNullAsZero", label: "Draw Missing Data as Zero", type: "bool",
		help: "Treats missing (null) data points as zero instead of leaving gaps. Useful for counters where no data means \"nothing happened\"."
	},
	{
		key: "hideLegend", label: "Legend", type: "bool", trueLabel: "Always hide", falseLabel: "Always show",
		help: "By default the legend below the graph is hidden automatically when there are more than 10 series. \"Always show\" forces it on; \"Always hide\" forces it off."
	},
	{ key: "hideGrid", label: "Hide Grid Lines", type: "bool", help: "Hides the background grid entirely." },
	{ key: "tz", label: "Time Zone", type: "string", help: "Time zone for the X axis, e.g. \"America/Denver\" or \"UTC\". Leave empty to use the Graphite server's local time zone." },
	{ key: "colorList", label: "Series Colors", type: "string", help: "Comma-separated color overrides applied to series in order, e.g. \"blue,green,#FF0000\". Leave empty for Graphite's default palette." }
];
/** Time range presets offered by the graph editor for the "from" parameter. */
export const TIME_RANGE_PRESETS = [
	{ value: "-1h", label: "Last Hour" },
	{ value: "-8h", label: "Last 8 Hours" },
	{ value: "-24h", label: "Last 24 Hours" },
	{ value: "-7d", label: "Last 7 Days" },
	{ value: "-14d", label: "Last 14 Days" },
	{ value: "-30d", label: "Last 30 Days" }
];
export const TIME_RANGE_HELP = "Relative ranges start with \"-\" followed by a number and unit: s (seconds), min (minutes), h (hours), d (days), w (weeks), mon (months), y (years). Examples: -30min, -8h, -14d. Absolute times like 20:00_20260101 (HH:MM_YYYYMMDD) also work.";

const CURATED_KEYS = {};
for (let opt of RENDER_OPTIONS)
	CURATED_KEYS[opt.key.toLowerCase()] = opt.key;
CURATED_KEYS["title"] = "title";
CURATED_KEYS["from"] = "from";
CURATED_KEYS["until"] = "until";
// Parameters that are stripped entirely: image dimensions belong to the viewing client, and
// cacheBust is a transient client-added parameter that must never be stored in configuration.
const STRIPPED_KEYS = { "width": true, "height": true, "cachebust": true };

/**
 * Parses one GraphiteConfig render query string into an editable graph model.
 * @param {String} queryString Raw query string from GraphiteConfig.json (no leading "?").
 * @returns {Object} Graph model:
 *   {
 *     targets: [ { raw: String, ast: Object|null, parseError: String|null } ],
 *     title: String, from: String, until: String,
 *     options: { <curated option key>: String },   // decoded string values; absent keys are unset
 *     otherParams: [ { key, value } ]              // preserved unknown parameters
 *   }
 */
export function ParseRenderQueryString(queryString)
{
	let model = { targets: [], title: "", from: "", until: "", options: {}, otherParams: [] };
	for (let pair of ParseQueryParams(queryString))
	{
		let keyLower = pair.key.toLowerCase();
		if (STRIPPED_KEYS[keyLower])
			continue;
		if (keyLower === "target")
		{
			let target = { raw: pair.value.trim(), ast: null, parseError: null };
			try
			{
				target.ast = ParseExpression(target.raw);
				target.raw = SerializeExpression(target.ast);
			}
			catch (ex)
			{
				target.parseError = ex.message;
			}
			model.targets.push(target);
		}
		else if (keyLower === "title")
			model.title = pair.value;
		else if (keyLower === "from")
			model.from = pair.value;
		else if (keyLower === "until")
			model.until = pair.value;
		else if (CURATED_KEYS[keyLower])
			model.options[CURATED_KEYS[keyLower]] = pair.value;
		else
			model.otherParams.push({ key: pair.key, value: pair.value });
	}
	return model;
}
/**
 * Serializes a graph model back into a GraphiteConfig render query string.
 * Never emits width or height; those are decided by the viewing client at render time.
 * @param {Object} model Graph model from ParseRenderQueryString.
 * @returns {String} Render query string.
 */
export function SerializeRenderQueryString(model)
{
	let pairs = [];
	for (let target of model.targets)
	{
		let raw = target.ast ? SerializeExpression(target.ast) : target.raw;
		if (raw && raw.trim())
			pairs.push({ key: "target", value: raw.trim() });
	}
	if (model.title)
		pairs.push({ key: "title", value: model.title });
	if (model.from)
		pairs.push({ key: "from", value: model.from });
	if (model.until)
		pairs.push({ key: "until", value: model.until });
	for (let opt of RENDER_OPTIONS)
	{
		let v = model.options[opt.key];
		if (typeof v !== "undefined" && v !== null && v !== "")
			pairs.push({ key: opt.key, value: String(v) });
	}
	for (let p of model.otherParams)
	{
		if (p.key)
			pairs.push({ key: p.key, value: p.value });
	}
	return BuildQueryParams(pairs);
}
/**
 * Returns a short human-readable caption for a graph model (its title, or a summary of its first target).
 * @param {Object} model Graph model.
 * @returns {String} Caption text.
 */
export function GetGraphCaption(model)
{
	if (model.title)
		return model.title;
	if (model.targets.length)
		return model.targets[0].raw;
	return "(empty graph)";
}
/**
 * Creates a new empty graph model with sensible defaults for the editor.
 * @returns {Object} Graph model.
 */
export function CreateEmptyGraphModel()
{
	return { targets: [], title: "", from: "-24h", until: "", options: {}, otherParams: [] };
}
/**
 * Deep-copies a graph model (plain JSON data).
 * @param {Object} model Graph model.
 * @returns {Object} Copy.
 */
export function CopyGraphModel(model)
{
	return JSON.parse(JSON.stringify(model));
}
